import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import { Tooltip } from "@itandi/itandi-bb-ui/Tooltip";
import type { ComponentProps } from "react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import type { KeyedMutator } from "swr";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { useCompanyVendors } from "~/hooks/useCompanyVendors";
import useItandiAccount from "~/hooks/useItandiAccount";
import useLoader from "~/hooks/useLoader";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { CompanyVendor } from "~/models/CompanyVendor";
import { getCompanyVendorNameWithStatus } from "~/models/CompanyVendor";
import type { Deal } from "~/models/Deal";
import { ESTIMATION_BY } from "~/models/Deal";
import { NOW_ACCEPTING, statusMovingValidation } from "~/models/Status";
import { VENDOR_COMPANY_ITANDI_ID } from "~/models/VendorCompany";
import { isSuccess } from "~/utils/api/api_handler";
import { create } from "~/utils/api/deals/DealVendorCompany";
import { AccordionHtml } from "../accordion_html/AccordionHtml";
import Styles from "./CompanyVendorSelector.module.scss";

type Props = {
	deal: Deal;
	mutateDeal: KeyedMutator<Deal>;
};

export function CompanyVendorSelector({ deal, mutateDeal }: Props) {
	const { Loader, showLoader, hideLoader } = useLoader();
	const { isOpen, handleOpen, handleClose } = useOpenHandler();

	const { data: companyVendors, isLoading } = useCompanyVendors().index();
	const { data: account } = useItandiAccount().currentAccount();

	// まだ一度も施工会社を選択していない場合がnull
	// 新規の案件では発生することはないが、過去の案件ではあり得る
	const savedDealVendorCompany = deal.dealVendorCompanies[0] ?? null;
	const savedCompanyVendor = savedDealVendorCompany.companyVendor ?? null;

	const [companyVendorId, setCompanyVendorId] = useState(
		savedCompanyVendor ? savedCompanyVendor.id : null
	);
	const [showTenantChecked, setShowTenantChecked] = useState(
		savedDealVendorCompany.showTenant
	);
	const [estimationByCompany, setEstimationByCompany] = useState(
		deal.estimationBy === ESTIMATION_BY.COMPANY
	);

	const selectedCompanyVendor = useMemo(() => {
		if (companyVendors === null) {
			return null;
		}
		return companyVendors.find((v) => v.id === companyVendorId) ?? null;
	}, [companyVendorId, companyVendors]);

	const getCheckboxDefaults = useCallback(
		(
			vendor: CompanyVendor
		): { estimationByCompany: boolean; showTenant: boolean } => {
			const isSavedVendor = vendor.id === savedCompanyVendor?.id;

			// 今設定(保存)されている施工会社は、保存されている設定を返す
			if (isSavedVendor) {
				return {
					estimationByCompany: deal.estimationBy === ESTIMATION_BY.COMPANY,
					showTenant: savedDealVendorCompany.showTenant
				};
			}

			// それ以外は、デフォルト設定を返す
			const estimationByCompanyDefault =
				!vendor.vendorEstimationRequired &&
				vendor.defaultEstimationBy === "company";

			return {
				estimationByCompany: estimationByCompanyDefault,
				showTenant: vendor.showTenantDefault
			};
		},
		[
			deal.estimationBy,
			savedCompanyVendor?.id,
			savedDealVendorCompany.showTenant
		]
	);

	const onCompanyVendorChange = useCallback<
		Required<ComponentProps<typeof SingleComboBox>>["onChange"]
	>(
		(value): void => {
			if (companyVendors) {
				const selectedCompanyVendorId = value?.value;
				if (selectedCompanyVendorId === undefined) {
					setCompanyVendorId(null);
					setShowTenantChecked(false);
					setEstimationByCompany(false);
					return;
				}

				const companyVendor = companyVendors.find(
					(c: CompanyVendor) => c.id === Number(selectedCompanyVendorId)
				)!;
				const checkBoxDefaults = getCheckboxDefaults(companyVendor);

				setCompanyVendorId(companyVendor.id);
				setShowTenantChecked(checkBoxDefaults.showTenant);
				setEstimationByCompany(checkBoxDefaults.estimationByCompany);
			}
		},
		[companyVendors, getCheckboxDefaults]
	);

	const forwardStatusDisabled = useMemo(() => {
		if (account === null || companyVendorId === null) return true;
		return !statusMovingValidation[NOW_ACCEPTING](account);
	}, [account, companyVendorId]);

	if (isLoading) {
		return null;
	}

	const handleAcceptForward = async (): Promise<void> => {
		handleClose();
		if (companyVendorId === null) return;
		showLoader();
		const result = await create(
			deal.id,
			companyVendorId,
			showTenantChecked,
			estimationByCompany
		);
		if (isSuccess(result)) {
			await mutateDeal();
			hideLoader();
			toast.success("更新しました");
			return;
		}
		hideLoader();
		toast.error("エラーが発生しました");
	};
	return (
		<div>
			<Loader />
			<div className={Styles.NavigationBlock}>
				<div className={Styles.NavigationContent}>
					{savedCompanyVendor !== null && (
						<p className={Styles.SavedCompanyVendor}>
							現在依頼中の施工会社:
							{getCompanyVendorNameWithStatus(savedCompanyVendor)}
						</p>
					)}
					<SingleComboBox
						maxMenuHeight={200}
						onChange={onCompanyVendorChange}
						options={
							companyVendors?.map((c: CompanyVendor) => ({
								label: getCompanyVendorNameWithStatus(c),
								value: String(c.id)
							})) ?? []
						}
						placeholder="選択してください"
						value={
							selectedCompanyVendor !== null
								? {
										label: getCompanyVendorNameWithStatus(
											selectedCompanyVendor
										),
										value: String(selectedCompanyVendor.id)
									}
								: null
						}
					/>
				</div>
			</div>
			{selectedCompanyVendor !== null &&
				selectedCompanyVendor.cautionaryNoteHtml !== "" && (
					<div className={Styles.NavigationBlock}>
						<div className={Styles.NavigationContent}>
							<span className={Styles.NavigationTitle}>注意事項</span>
							<AccordionHtml
								html={selectedCompanyVendor.cautionaryNoteHtml}
								maxHeightForClosed={150}
							/>
						</div>
					</div>
				)}
			{companyVendorId !== null && (
				<>
					{selectedCompanyVendor?.vendorCompany?.id !==
						VENDOR_COMPANY_ITANDI_ID && (
						<div className={Styles.NavigationBlock}>
							<InputCheckBox
								checked={showTenantChecked}
								label="施工会社に入居者の情報を表示する"
								onChange={(): void => {
									setShowTenantChecked(!showTenantChecked);
								}}
							/>
						</div>
					)}
					{!selectedCompanyVendor?.vendorEstimationRequired &&
						!selectedCompanyVendor?.dealManagementTypeOwn && (
							<div className={Styles.NavigationBlock}>
								<InputCheckBox
									checked={estimationByCompany}
									disabled={selectedCompanyVendor?.vendorEstimationRequired}
									label="管理会社にて見積作成を行う"
									onChange={(): void => {
										setEstimationByCompany(!estimationByCompany);
									}}
								/>
								{selectedCompanyVendor?.vendorEstimationRequired ? (
									<Tooltip>
										この施工会社はシステム上見積入力が必須に設定されています
									</Tooltip>
								) : null}
							</div>
						)}
				</>
			)}
			<div className={Styles.StatusHandleButtonRow}>
				<div className={Styles.RightButtons}>
					<button
						className={Styles.ForwardButton}
						disabled={forwardStatusDisabled}
						onClick={handleOpen}
						type="button"
					>
						設定を更新する
					</button>
				</div>
				<ConfirmModal
					actionText="確定"
					confirmType="save"
					handleAccept={handleAcceptForward}
					handleCancel={handleClose}
					isOpen={isOpen}
				>
					施工会社を
					{deal.dealVendorCompanies.length === 0 ? "設定" : "変更"}
					してよろしいですか？
				</ConfirmModal>
			</div>
		</div>
	);
}
